import React, { useState, useEffect } from 'react'
import CustomerRegistration from './CustomerRegistration'
import SignUpForm from './PartnerRegistration'
import logo from '../assets/images/logo_new.jpeg'
import { motion, AnimatePresence } from 'framer-motion'
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome"
import { faUserTie, faUsers, faBuilding, faHandshake, faStar, faShieldAlt, faChartLine } from "@fortawesome/free-solid-svg-icons"

const RegistrationPage = () => {
  const [activeTab, setActiveTab] = useState('partner');

  useEffect(() => {
    window.scrollTo(0, 0);
  }, []);

  return (
    <div className="min-h-screen bg-[#EDE9E0] relative overflow-hidden">
      {/* Subtle Background Pattern */}
      <div 
        className="absolute inset-0 opacity-40 pointer-events-none"
        style={{
          backgroundImage: `radial-gradient(circle at 25% 30%, #D4C9A8 1px, transparent 1px)`,
          backgroundSize: '35px 35px'
        }}
      />

      <div className="relative z-10 py-8 sm:py-12 md:py-16 px-4 sm:px-6">
        {/* Logo Section */}
        <motion.div 
          initial={{ opacity: 0, y: -20 }}
          animate={{ opacity: 1, y: 0 }}
          className="text-center mb-8"
        >
          <motion.div 
            whileHover={{ scale: 1.05 }}
            className="inline-block mb-4"
          >
            <img 
              src={logo} 
              alt="Dharaa Pariwar Logo" 
              className="h-20 sm:h-24 md:h-28 w-auto mx-auto drop-shadow-sm"
            />
          </motion.div>
          <h1 className="text-4xl md:text-5xl font-bold text-[#2C3A2A] tracking-tight">
            Dharaa Pariwar
          </h1>
          <p className="text-[#5F6A5A] mt-2 text-lg">Join Our Growing Family</p>
        </motion.div>

        {/* Neomorphic Tab Container */}
        <div className="max-w-3xl mx-auto mb-10">
          <div className="bg-[#EDE9E0] p-3 rounded-3xl shadow-[8px_8px_20px_#d4c9b8,-8px_-8px_20px_#ffffff] border border-[#E5E0D0]">
            <div className="flex flex-col sm:flex-row gap-3">
              {/* Partner Tab */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setActiveTab('partner')}
                className={`flex-1 py-5 px-6 rounded-2xl font-semibold text-lg flex items-center justify-center gap-3 transition-all ${
                  activeTab === 'partner'
                    ? 'bg-[#8F8431] text-white shadow-[inset_4px_4px_10px_#7A7130,inset_-4px_-4px_10px_#A89A5E]'
                    : 'bg-[#EDE9E0] text-[#2C3A2A] shadow-[6px_6px_14px_#d4c9b8,-6px_-6px_14px_#ffffff] hover:shadow-[4px_4px_10px_#d4c9b8,-4px_-4px_10px_#ffffff]'
                }`}
              >
                <FontAwesomeIcon icon={faUserTie} className="text-xl" />
                Partner Registration
              </motion.button>

              {/* Customer Tab */}
              <motion.button
                whileHover={{ scale: 1.02 }}
                whileTap={{ scale: 0.97 }}
                onClick={() => setActiveTab('customer')}
                className={`flex-1 py-5 px-6 rounded-2xl font-semibold text-lg flex items-center justify-center gap-3 transition-all ${
                  activeTab === 'customer'
                    ? 'bg-[#8F8431] text-white shadow-[inset_4px_4px_10px_#7A7130,inset_-4px_-4px_10px_#A89A5E]'
                    : 'bg-[#EDE9E0] text-[#2C3A2A] shadow-[6px_6px_14px_#d4c9b8,-6px_-6px_14px_#ffffff] hover:shadow-[4px_4px_10px_#d4c9b8,-4px_-4px_10px_#ffffff]'
                }`}
              >
                <FontAwesomeIcon icon={faUsers} className="text-xl" />
                Customer Registration
              </motion.button>
            </div>
          </div>
        </div>

        {/* Form Container */}
        <AnimatePresence mode="wait">
          <motion.div
            key={activeTab}
            initial={{ opacity: 0, y: 30 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -30 }}
            transition={{ duration: 0.4 }}
            className="max-w-3xl mx-auto"
          >
            {activeTab === 'partner' ? <SignUpForm /> : <CustomerRegistration />}
          </motion.div>
        </AnimatePresence>

        {/* Benefits Section */}
        <div className="max-w-6xl mx-auto mt-16 grid grid-cols-1 md:grid-cols-2 gap-8 px-4">
          {/* Partner Benefits */}
          <motion.div 
            initial={{ opacity: 0, x: -30 }}
            animate={{ opacity: 1, x: 0 }}
            whileHover={{ y: -6 }}
            className="bg-[#EDE9E0] p-8 rounded-3xl shadow-[10px_10px_24px_#d4c9b8,-10px_-10px_24px_#ffffff] border border-[#E5E0D0]"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-[#8F8431] flex items-center justify-center shadow-[4px_4px_10px_#d4c9b8,-4px_-4px_10px_#ffffff]">
                <FontAwesomeIcon icon={faHandshake} className="text-white text-2xl" />
              </div>
              <h3 className="text-2xl font-bold text-[#2C3A2A]">Partner Benefits</h3>
            </div>
            
            <div className="space-y-5">
              {[
                { icon: faStar, text: "Access to exclusive property listings" },
                { icon: faChartLine, text: "Higher commission rates" },
                { icon: faBuilding, text: "Professional marketing tools" },
                { icon: faShieldAlt, text: "Dedicated support team" }
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 text-[#385532]">
                  <div className="mt-1 w-6 h-6 rounded-lg bg-[#C7C091]/20 flex items-center justify-center flex-shrink-0">
                    <FontAwesomeIcon icon={item.icon} className="text-[#8F8431]" />
                  </div>
                  <span className="text-base">{item.text}</span>
                </div>
              ))}
            </div>
          </motion.div>

          {/* Customer Benefits */}
          <motion.div 
            initial={{ opacity: 0, x: 30 }}
            animate={{ opacity: 1, x: 0 }}
            whileHover={{ y: -6 }}
            className="bg-[#EDE9E0] p-8 rounded-3xl shadow-[10px_10px_24px_#d4c9b8,-10px_-10px_24px_#ffffff] border border-[#E5E0D0]"
          >
            <div className="flex items-center gap-4 mb-6">
              <div className="w-14 h-14 rounded-2xl bg-[#8F8431] flex items-center justify-center shadow-[4px_4px_10px_#d4c9b8,-4px_-4px_10px_#ffffff]">
                <FontAwesomeIcon icon={faUsers} className="text-white text-2xl" />
              </div>
              <h3 className="text-2xl font-bold text-[#2C3A2A]">Customer Benefits</h3>
            </div>
            
            <div className="space-y-5">
              {[
                { icon: faStar, text: "Priority property access" },
                { icon: faStar, text: "Personalized property alerts" },
                { icon: faHandshake, text: "Expert consultation" },
                { icon: faChartLine, text: "Special offers and discounts" }
              ].map((item, i) => (
                <div key={i} className="flex items-start gap-4 text-[#385532]">
                  <div className="mt-1 w-6 h-6 rounded-lg bg-[#C7C091]/20 flex items-center justify-center flex-shrink-0">
                    <FontAwesomeIcon icon={item.icon} className="text-[#8F8431]" />
                  </div>
                  <span className="text-base">{item.text}</span>
                </div>
              ))}
            </div>
          </motion.div>
        </div>

        {/* Footer */}
        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          className="text-center mt-16"
        >
          <p className="text-[#5F6A5A]">
            Already have an account?{" "}
            <a href="/login" className="text-[#8F8431] font-semibold hover:underline">
              Sign in here
            </a>
          </p>
          <p className="text-[#9A9A8A] text-xs mt-4">
            © {new Date().getFullYear()} Dharaa Pariwar. All rights reserved.
          </p>
        </motion.div>
      </div>
    </div>
  )
}

export default RegistrationPage